import React, { useEffect, useState } from "react";

import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { getCognitoUserData, selectCompanyId, selectUserRole } from "../../features/auth/authSlice";
import {
  createCampaign,
  fetchCampaigns,
  hideModal,
  ICampaign,
  selectCampaignStatus,
  selectDisplayModal,
  selectCreatedCampaign,
} from "../../features/campaign/campaignSlice";
import { ConditionEnum } from "../../features/common/enums";
import {
  fetchCompanies,
  selectCompanies,
} from "../../features/company/companySlice";
import { AfterActions, DialogPopUp } from "../common/DialogPopUp";
import { MainLayout } from "../common/Layouts/Main";
import { CampaignForm, InitialValues } from "./Form";
import { analytics } from "../../services/analytics";
import { Prefixes } from "../prefixes";
import { useHistory } from "react-router-dom";

import { FormFooter } from "../common/FormFooter";
import { getTranslations } from "../../features/translations/translationsUtils";
import { UserRole } from "../../features/users/usersSlice";

const EMPTY_VALUE = {
  title: "",
  description: "",
  fromDate: "",
  toDate: "",
  companyId: undefined,
  rangeDate: undefined,
};

export const NewCampaign: React.FunctionComponent = () => {
  const [restart, setRestart] = useState(true);
  const history = useHistory();
  const userRole = useSelector(selectUserRole);
  const companyId = useSelector(selectCompanyId);
  const [initialValues, setInitialValues] = useState<InitialValues>({ ...EMPTY_VALUE });
  const [currentAction, setCurrentAction] = useState("");
  const [form] = Form.useForm();
  const companiesDict = useSelector(selectCompanies);
  const isModalVisible = useSelector(selectDisplayModal);
  const isLoading = useSelector(selectCampaignStatus);
  const cognitoData = useSelector(getCognitoUserData);
  const cognitoUsername = cognitoData ? cognitoData['cognito:username'] : null;
  const createdCampaign = useSelector(selectCreatedCampaign);
  const dispatch = useDispatch();

  useEffect(() => {
    if (createdCampaign) {
      analytics.createPromoBanner({
        userName: cognitoUsername,
        companyName: companiesDict[createdCampaign.companyId]?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdCampaign]);

  useEffect(() => {
    dispatch(fetchCampaigns());
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (restart) {
      form.resetFields();
      form.setFieldsValue({});
      setInitialValues({ ...EMPTY_VALUE });
      setRestart(false);
    }
  }, [restart, form]);

  const handleClose = () => {
    history.push(`${Prefixes.campaign}`);
  };

  const handleFinish = (campaign: ICampaign) => {
    dispatch(createCampaign(campaign));
    form.setFieldsValue(EMPTY_VALUE);
  };

  const handleSave = (afterAction: AfterActions, publish?: ConditionEnum) => {
    if (initialValues) {
      form
        .validateFields()
        .then((values) => {
          const fromDate = values.rangeDate
            ? values.rangeDate[0].format('YYYY-MM-DD')
            : "";
          const toDate = values.rangeDate ? values.rangeDate[1].format('YYYY-MM-DD') : "";
          if (userRole !== UserRole.SuperAdmin) {
            values.companyId = companyId;
          }
          const newCampaign: ICampaign = {
            _id: "",
            image: initialValues.image,
            companyId: values.companyId,
            survey: initialValues.survey,
            title: values.title,
            description: values.description,
            fromDate,
            toDate,
            published: publish ? publish : undefined,
          };
          if (initialValues.survey && initialValues.resultsVisibilityRole) {
            newCampaign.resultsVisibilityRole =
              initialValues.resultsVisibilityRole;
          }
          setCurrentAction(afterAction);
          handleFinish(newCampaign);
        })
        .catch((_error) => { });
    }
  };

  if (restart) {
    return null;
  }

  return (
    <MainLayout
      title={getTranslations('CAMPAIGN_CREATE')}
      onGoBack={() => {
        history.push(`${Prefixes.campaign}`);
      }}
      breadcrumbs={[getTranslations('CAMPAIGN_TITLE'), getTranslations('CAMPAIGN_CREATE_SHORT')]}
    >
      <DialogPopUp
        name={getTranslations('CAMPAIGN_TITLE')}
        action={getTranslations('CAMPAIGN_CREATED')}
        visible={isModalVisible}
        onCancel={() => {
          dispatch(hideModal());
          if (currentAction === AfterActions.BACK) {
            history.push(`${Prefixes.campaign}`);
            return;
          }
          setRestart(true);
        }}
      />
      <CampaignForm
        form={form}
        initialValues={initialValues}
        onUpdateValues={setInitialValues}
        companyId={companyId}
      />
      <FormFooter
        editing={false}
        objectName={getTranslations('CAMPAIGN_TITLE_SINGULAR').toLowerCase()}
        disabledSave={isLoading || !initialValues?.survey}
        onCancel={handleClose}
        onSave={() => {
          handleSave(AfterActions.BACK)
        }}
        onSaveAndCreateAnother={() => {
          handleSave(AfterActions.STAY)
        }}
      />
    </MainLayout>
  );
};
